/* eslint-disable */
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import PompiLogo from '../../assets/PompiImage.svg';
import PrimaryButton from '../../components/PrimaryButton';
import {card, colors, radii, spacing, typography} from '../../config/theme';
import {answerQuestions} from '../../slice/answerSlice';
import {getEstimateSavings} from '../../services/auth-service';

// HARD-CODED for now. These are the numbers that must become dynamic.
// Keep the same keys when the API is ready so the UI doesn't change.
const DEFAULT_STATS = {
  currency: '€',
  avgCityPrice: 1.95,
  pompiBestPrice: 1.65,
  verifiedMinutesAgo: 2,
  avgFillLitres: 50,
  rating: '4.9',
  driversCount: '120k',
};

const AfterSignUp = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const res = await getEstimateSavings();
        setMessage(res?.message || '');

        // TODO(API): once the backend returns the numbers, merge them here.
        // Missing fields fall back to DEFAULT_STATS automatically.
        // setStats(prev => ({...prev, ...res?.stats}));
      } catch (e) {
        // Keep DEFAULT_STATS so the onboarding still shows when the API fails.
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const c = stats.currency;
  const savePerFill = Math.round(
    (stats.avgCityPrice - stats.pompiBestPrice) * stats.avgFillLitres,
  );

  const handleStart = () => dispatch(answerQuestions());

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}>
        <View style={styles.top}>
          <PompiLogo width={160} height={145} />

          <View style={styles.topBadge}>
            <Text style={styles.topBadgeText}>
              {t('root.AfterSignUp.badge', '⚡ SAVE MONEY ON FUEL')}
            </Text>
          </View>

          <Text style={styles.title}>
            {t('root.AfterSignUp.headline', 'STOP PAYING TOO MUCH.')}
          </Text>
          <Text style={styles.subtitle}>
            {t(
              'root.AfterSignUp.subheadline',
              'Find the cheapest pump in seconds.',
            )}
          </Text>

          {isLoading ? (
            <ActivityIndicator
              size="large"
              color={colors.primary}
              style={styles.loader}
            />
          ) : (
            <View style={styles.card}>
              {/* Other stations */}
              <View style={[styles.row, styles.badRow]}>
                <View style={styles.rowLeft}>
                  <Text style={styles.badLabel}>
                    ✕  {t('root.AfterSignUp.otherStations', 'OTHER STATIONS')}
                  </Text>
                  <Text style={styles.badSub}>
                    {t('root.AfterSignUp.avgPump', 'Average city pump')}
                  </Text>
                </View>
                <View style={styles.priceCol}>
                  <Text style={styles.badPrice}>
                    {c}
                    {stats.avgCityPrice.toFixed(2)}
                  </Text>
                  <Text style={styles.badPerL}>/ L</Text>
                </View>
              </View>

              {/* Pompi best price */}
              <View style={[styles.row, styles.goodRow]}>
                <View style={styles.rowLeft}>
                  <Text style={styles.goodLabel}>
                    ✓  {t('root.AfterSignUp.bestPrice', 'POMPI BEST PRICE')}
                  </Text>
                  <Text style={styles.goodSub}>
                    {t(
                      'root.AfterSignUp.verified',
                      'Verified {{min}} min ago',
                      {min: stats.verifiedMinutesAgo},
                    )}
                  </Text>
                </View>
                <View style={styles.priceCol}>
                  <Text style={styles.goodPrice}>
                    {c}
                    {stats.pompiBestPrice.toFixed(2)}
                  </Text>
                  <Text style={styles.goodPerL}>/ L</Text>
                </View>
              </View>

              <View style={styles.saveBadge}>
                <Text style={styles.saveBadgeText}>
                  {t('root.AfterSignUp.save', 'SAVE {{amount}} PER FILL-UP', {
                    amount: `${c}${savePerFill}`,
                  })}
                </Text>
              </View>
            </View>
          )}

          {!!message && <Text style={styles.message}>{message}</Text>}

          <View style={styles.chips}>
            <Chip text={`⭐ ${stats.rating} ${t('root.AfterSignUp.rating', 'Rating')}`} />
            <Chip text={`🚗 ${stats.driversCount} ${t('root.AfterSignUp.drivers', 'Drivers')}`} />
            <Chip text={`✔ ${t('root.AfterSignUp.free', 'Free to try')}`} />
          </View>
        </View>

        <View style={styles.bottom}>
          <PrimaryButton
            title={t('root.AfterSignUp.start')}
            onPress={handleStart}
          />
          <Text style={styles.footnote}>
            {t('root.AfterSignUp.footnote', 'Takes 30 seconds • No credit card')}
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const Chip = ({text}) => (
  <View style={styles.chip}>
    <Text style={styles.chipText}>{text}</Text>
  </View>
);

// NOTE: no fontWeight anywhere. Weight comes from the PlusJakartaSans font
// files via typography tokens (see config/theme.js).
const styles = StyleSheet.create({
  safe: {flex: 1, backgroundColor: colors.background},
  scroll: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  top: {alignItems: 'center', gap: spacing.md},
  loader: {marginVertical: spacing.xl},

  // The logo already contains the "pompi" wordmark, so the badge sits
  // below it instead of overlapping.
  topBadge: {
    backgroundColor: colors.primary,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 2,
  },
  topBadgeText: {...typography.labelSm, color: colors.onPrimary},

  title: {
    ...typography.displayMobile,
    color: colors.primary,
    textAlign: 'center',
  },
  subtitle: {...typography.bodyLg, color: colors.textMuted, textAlign: 'center'},

  // Comparison card
  card: {...card, width: '100%', padding: spacing.sm + 2},
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md + 4,
    borderRadius: radii.sm,
  },
  rowLeft: {flexShrink: 1, paddingRight: spacing.sm},
  priceCol: {alignItems: 'flex-end'},

  badRow: {
    backgroundColor: colors.errorTint,
    borderWidth: 1,
    borderColor: colors.errorContainer,
  },
  badLabel: {...typography.labelMd, color: colors.error},
  badSub: {...typography.bodySm, color: colors.textMuted, marginTop: spacing.xs},
  badPrice: {
    ...typography.headlineLg,
    color: colors.error,
    textDecorationLine: 'line-through',
  },
  badPerL: {...typography.labelMd, color: colors.error},

  goodRow: {
    backgroundColor: colors.primary,
    marginTop: spacing.sm + 2,
    paddingBottom: spacing.lg + 6, // room for the overlapping save badge
  },
  goodLabel: {...typography.labelMd, color: colors.tertiary},
  goodSub: {
    ...typography.bodySm,
    color: colors.onPrimaryMuted,
    marginTop: spacing.xs,
  },
  goodPrice: {...typography.displayLg, color: colors.tertiary},
  goodPerL: {...typography.labelMd, color: colors.tertiary},

  saveBadge: {
    alignSelf: 'center',
    marginTop: -spacing.md - 2,
    backgroundColor: colors.tertiary,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md + 2,
    paddingVertical: spacing.xs + 2,
  },
  saveBadgeText: {...typography.labelSm, color: colors.primary},

  message: {
    ...typography.bodyMd,
    color: colors.primary,
    textAlign: 'center',
  },

  // Stat chips
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  chip: {
    backgroundColor: colors.surfaceMuted,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 3,
  },
  chipText: {...typography.labelMd, color: colors.text},

  // CTA
  bottom: {marginTop: spacing.xl},
  footnote: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: spacing.md,
  },
});

export default AfterSignUp;